import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * TouchButton - Accessible large-touch button component for kiosk UI
 * 
 * Variants:
 * - primary: Orange gradient CTA (Add to Cart, Checkout, Pay)
 * - secondary: Dark with border (Cancel, Back to Menu)
 * - ghost: Transparent (Close, Back)
 * - icon: Square icon-only button
 * - selection: Large card-style selection button
 */
export default function TouchButton({
  children,
  variant = 'primary',
  size = 'default',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  selected = false,
  fullWidth = false,
  className = '',
  onClick,
  type = 'button',
  'aria-label': ariaLabel,
  ...props
}) {
  // Base classes shared by all variants
  const baseClasses = `
    inline-flex items-center justify-center
    font-semibold
    transition-all duration-150 ease-out
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-[0.98]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f5a623] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f1419]
    select-none
  `.trim();

  // Variant-specific styles
  const variants = {
    primary: `
      h-16 min-w-[200px] px-8
      text-lg text-[#0f1419]
      bg-gradient-to-br from-[#f5a623] to-[#ff6b35]
      border-none rounded-2xl
      shadow-lg shadow-black/30
      hover:brightness-110 hover:shadow-xl hover:shadow-[#f5a623]/20
    `,
    secondary: `
      h-14 min-w-[160px] px-6
      text-base text-white
      bg-[#252b33]
      border border-white/10 rounded-xl
      hover:bg-[#2d343d] hover:border-white/20
    `,
    ghost: `
      h-12 px-4
      text-sm text-gray-400
      bg-transparent
      border-none rounded-lg
      hover:text-white hover:bg-white/10
    `,
    icon: `
      w-14 h-14 p-0
      text-gray-400
      bg-transparent
      border-none rounded-xl
      hover:text-white hover:bg-white/10
    `,
    selection: `
      w-full p-8
      flex-col gap-4
      text-white
      bg-[#1a1f26]
      border-2 rounded-3xl
      ${selected 
        ? 'border-[#f5a623] bg-[#f5a623]/10' 
        : 'border-white/10 hover:border-white/20 hover:bg-[#252b33]'
      }
    `,
  };

  // Size variations
  const sizes = {
    small: 'h-10 px-4 text-sm min-w-[120px]',
    default: '', // Use variant defaults
    large: 'h-20 px-10 text-xl min-w-[240px]',
  };

  // Determine final classes
  const variantClasses = variants[variant] || variants.primary;
  const sizeClasses = size !== 'default' ? sizes[size] : '';
  const widthClasses = fullWidth ? 'w-full' : '';

  // Handle click with loading state
  const handleClick = (e) => {
    if (loading || disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  // Determine icon size based on variant
  const iconSize = variant === 'selection' ? 48 : variant === 'icon' ? 24 : 20;

  return (
    <button
      type={type}
      className={`
        ${baseClasses}
        ${variantClasses}
        ${sizeClasses}
        ${widthClasses}
        ${className}
      `.trim()}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-disabled={disabled || loading}
      aria-pressed={variant === 'selection' ? selected : undefined}
      {...props}
    >
      {loading ? (
        <Loader2 className="animate-spin" size={iconSize} />
      ) : (
        <>
          {Icon && iconPosition === 'left' && (
            <Icon size={iconSize} className={children ? 'mr-2' : ''} />
          )}
          {children}
          {Icon && iconPosition === 'right' && (
            <Icon size={iconSize} className={children ? 'ml-2' : ''} />
          )}
        </>
      )}
    </button>
  );
}

/**
 * SelectionButton - Preset for order type selection cards
 */
export function SelectionButton({
  icon,
  label,
  sublabel,
  selected,
  onClick,
  disabled,
  className = '',
}) {
  return (
    <TouchButton
      variant="selection"
      selected={selected}
      onClick={onClick}
      disabled={disabled}
      className={className}
      aria-label={`${label}${sublabel ? ` - ${sublabel}` : ''}`}
    >
      {icon && <span className="text-5xl">{icon}</span>}
      <span className="text-2xl font-semibold">{label}</span>
      {sublabel && (
        <span className="text-base text-gray-400">{sublabel}</span>
      )}
    </TouchButton>
  );
}

/**
 * IconButton - Preset for icon-only buttons (back, close, etc.)
 */
export function IconButton({
  icon: Icon,
  onClick,
  disabled,
  'aria-label': ariaLabel,
  className = '',
}) {
  return (
    <TouchButton
      variant="icon"
      icon={Icon}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={className}
    />
  );
}
